import { useEffect, useRef, useCallback } from 'react';
import { useClipStore, Message } from '../stores/clipStore';
import { decrypt, encrypt, arrayBufferToBase64 } from '../crypto';
import {
  encryptFileInChunks,
  decryptChunk,
  generateFileId,
  generateVideoThumbnail,
  EncryptedChunk,
  FileManifest,
} from '../crypto/chunked';

interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

const WS_URL = import.meta.env.VITE_WS_URL || '';

export function useWebSocket() {
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 1000;

  const store = useClipStore();

  const connect = useCallback(() => {
    if (!store.uuid || !store.authToken) {
      console.log('[WebSocket] Cannot connect: missing uuid or authToken');
      return;
    }

    // Close existing connection
    if (store.ws) {
      store.ws.close();
      store.setWs(null);
    }

    store.setConnecting(true);
    store.clearError();

    // Build WebSocket URL
    let wsUrl: string;
    if (WS_URL) {
      wsUrl = `${WS_URL}/ws/clips/${store.uuid}`.replace(/^http/, 'ws');
    } else {
      // Use current host with wss:// protocol
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws/clips/${store.uuid}`;
    }

    console.log('[WebSocket] Connecting to:', wsUrl);

    try {
      const socket = new WebSocket(wsUrl);
      store.setWs(socket);
      console.log('[WebSocket] Socket created');

      socket.onopen = () => {
        console.log('[WebSocket] Connected');
        store.setConnected(true);
        store.setConnecting(false);
        reconnectAttempts.current = 0;

        // Send auth
        console.log('[WebSocket] Sending auth...');
        socket.send(
          JSON.stringify({
            type: 'auth',
            token: store.authToken,
          })
        );
      };

      socket.onmessage = async (event) => {
        console.log('[WebSocket] Received message:', event.data);
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          await handleMessage(data);
        } catch (e) {
          console.error('[WebSocket] Failed to handle message:', e);
        }
      };

      socket.onclose = (event) => {
        console.log('[WebSocket] Closed:', event.code, event.reason);
        store.setConnected(false);
        store.setWs(null);

        // Attempt reconnect with exponential backoff
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current);
          console.log(`[WebSocket] Reconnecting in ${delay}ms...`);
          reconnectTimeout.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      socket.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        store.setError('Connection error');
      };
    } catch (e) {
      console.error('[WebSocket] Exception:', e);
      store.setConnecting(false);
      store.setError('Failed to connect');
    }
  }, [store.uuid, store.authToken]);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    if (store.ws) {
      store.ws.close();
      store.setWs(null);
    }
  }, [store.ws]);

  const handleMessage = async (data: WebSocketMessage) => {
    switch (data.type) {
      case 'connected':
        store.setMySenderId(data.senderId);
        break;

      case 'auth_ok':
        // Auth successful
        break;

      case 'auth_error':
        store.setError(data.message);
        disconnect();
        break;

      case 'history':
        // Decrypt history
        if (store.cryptoKey && data.messages) {
          const decryptedMessages = await Promise.all(
            data.messages.map(async (msg: Message) => {
              try {
                if (msg.type === 'text' && msg.content) {
                  const decrypted = await decrypt(store.cryptoKey!, msg.content);
                  return { ...msg, content: decrypted };
                }
                // Handle file_manifest type - need to decrypt filename
                if (msg.type === 'file_manifest' && msg.manifest) {
                  try {
                    const decryptedFilename = await decrypt(store.cryptoKey!, msg.manifest.filename);
                    return {
                      ...msg,
                      manifest: {
                        ...msg.manifest,
                        filename: decryptedFilename,
                      },
                    };
                  } catch (e) {
                    console.warn('[WebSocket] Failed to decrypt manifest filename:', e);
                  }
                }
                return msg;
              } catch (e) {
                return { ...msg, content: '[Decryption failed]' };
              }
            })
          );
          store.setMessages(decryptedMessages);
        }
        break;

      case 'peers':
        store.setPeerCount(data.count);
        break;

      case 'message':
        if (store.cryptoKey && data.message?.content) {
          try {
            const decrypted = await decrypt(store.cryptoKey, data.message.content);
            store.addMessage({ ...data.message, content: decrypted });
          } catch (e) {
            store.addMessage({ ...data.message, content: '[Decryption failed]' });
          }
        }
        break;

      case 'image':
        if (store.cryptoKey && data.message) {
          try {
            // Decrypt the filename if it's encrypted
            let decryptedFilename = data.message.filename;
            if (data.message.filename) {
              try {
                decryptedFilename = await decrypt(store.cryptoKey, data.message.filename);
              } catch (e) {
                console.warn('[WebSocket] Failed to decrypt filename, using as-is:', e);
                // If decryption fails, it might be an old message with plaintext filename
              }
            }
            store.addMessage({
              ...data.message,
              filename: decryptedFilename
            });
          } catch (e) {
            console.error('[WebSocket] Failed to handle image message:', e);
            // Add message without decryption as fallback
            store.addMessage(data.message);
          }
        } else {
          store.addMessage(data.message);
        }
        break;

      case 'file_manifest':
        if (store.cryptoKey && data.message?.manifest) {
          try {
            const manifest = data.message.manifest as FileManifest;

            // Decrypt filename
            const decryptedFilename = await decrypt(store.cryptoKey, manifest.filename);
            const decryptedManifest: FileManifest = {
              ...manifest,
              filename: decryptedFilename,
            };

            // Add to messages - VideoMessage component will manage the assembler
            store.addMessage({
              ...data.message,
              manifest: decryptedManifest,
            });
          } catch (e) {
            console.error('[WebSocket] Failed to handle file manifest:', e);
            store.addMessage(data.message);
          }
        } else {
          store.addMessage(data.message);
        }
        break;

      case 'file_chunk':
        // Legacy: file_chunk messages are stored but not displayed directly
        // VideoMessage component will collect and assemble them
        if (store.cryptoKey && data.message?.chunkInfo && data.message?.data) {
          try {
            const chunkInfo = data.message.chunkInfo;
            const encryptedChunk: EncryptedChunk = {
              index: chunkInfo.index,
              total: chunkInfo.total,
              data: data.message.data,
              iv: '', // IV is included in data
            };

            // Decrypt chunk data before storing
            const decryptedData = await decryptChunk(store.cryptoKey, encryptedChunk);

            // Store decrypted chunk in message (base64 encoded for storage)
            const decryptedChunkData = arrayBufferToBase64(decryptedData);

            // Add chunk message with decrypted data
            store.addMessage({
              ...data.message,
              data: decryptedChunkData,
            });
          } catch (e) {
            console.error('[WebSocket] Failed to handle file chunk:', e);
          }
        }
        break;

      case 'image_start':
        // New chunked protocol: image_start message
        if (store.cryptoKey && data.message) {
          try {
            // Decrypt filename if present
            let decryptedFilename = data.message.filename;
            if (data.message.filename) {
              try {
                decryptedFilename = await decrypt(store.cryptoKey, data.message.filename);
              } catch (e) {
                console.warn('[WebSocket] Failed to decrypt filename, using as-is:', e);
              }
            }

            store.addMessage({
              ...data.message,
              filename: decryptedFilename,
            });
          } catch (e) {
            console.error('[WebSocket] Failed to handle image_start:', e);
            store.addMessage(data.message);
          }
        }
        break;

      case 'image_chunk':
        // New chunked protocol: image_chunk message
        // Store as-is, will be assembled by EncryptedImage component
        if (data.message) {
          store.addMessage(data.message);
        }
        break;

      case 'image_complete':
        // New chunked protocol: image_complete message
        // Store as-is, will trigger assembly by EncryptedImage component
        if (data.message) {
          store.addMessage(data.message);
        }
        break;
    }
  };

  // Read ws from store - no closure issues since we access store directly
  const sendMessage = async (content: string) => {
    const { ws: socket, cryptoKey, connected } = useClipStore.getState();

    console.log('[sendMessage] Checking conditions:', {
      hasWs: !!socket,
      hasCryptoKey: !!cryptoKey,
      isConnected: connected,
    });

    if (!socket || !cryptoKey || !connected) {
      console.log('[sendMessage] Cannot send - conditions not met');
      return false;
    }

    try {
      console.log('[sendMessage] Encrypting message...');
      const encrypted = await encrypt(cryptoKey, content);
      console.log('[sendMessage] Encrypted, sending via WebSocket...');
      socket.send(
        JSON.stringify({
          type: 'message',
          content: encrypted,
        })
      );
      console.log('[sendMessage] Message sent successfully');
      return true;
    } catch (e) {
      console.error('[sendMessage] Error sending message:', e);
      return false;
    }
  };

  // Compress image to target size (default 900KB to leave room for encryption overhead)
  const compressImage = async (file: File, targetSizeKB: number = 900): Promise<Blob> => {
    console.log('[compressImage] Starting compression, target:', targetSizeKB, 'KB, type:', file.type);

    // If already small enough, return as-is
    if (file.size <= targetSizeKB * 1024) {
      console.log('[compressImage] File already small enough');
      return file;
    }

    // Only compress JPEG and PNG images
    const isPNG = file.type === 'image/png';
    const isJPEG = file.type === 'image/jpeg' || file.type === 'image/jpg';

    if (!isPNG && !isJPEG) {
      console.log('[compressImage] Unsupported image type, returning as-is');
      return file;
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        // Start with original dimensions
        let width = img.width;
        let height = img.height;

        // Calculate max dimensions based on file size
        // Rough estimate: larger images need more aggressive reduction
        const fileSizeMB = file.size / (1024 * 1024);
        let maxDimension = 2048;

        if (fileSizeMB > 5) maxDimension = 1280;
        else if (fileSizeMB > 3) maxDimension = 1600;
        else if (fileSizeMB > 1) maxDimension = 1920;

        // Scale down if needed
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
          console.log('[compressImage] Resized to:', width, 'x', height);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // For JPEG: fill white background (JPEG doesn't support transparency)
        // For PNG: don't fill background (preserve transparency)
        if (isJPEG) {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);

        // Determine output type and extension
        const outputType = isPNG ? 'image/png' : 'image/jpeg';

        // Try different quality levels
        // PNG doesn't support quality parameter, so we only try once for PNG
        const tryCompress = (quality: number): void => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Canvas toBlob failed'));
                return;
              }

              console.log('[compressImage] Quality:', quality, 'Result size:', Math.round(blob.size / 1024), 'KB, type:', outputType);

              // For PNG: accept the result since we can't adjust quality
              // For JPEG: try lower quality if still too large
              if (blob.size <= targetSizeKB * 1024 || quality <= 0.3 || isPNG) {
                resolve(blob);
              } else {
                // Try lower quality
                tryCompress(quality - 0.1);
              }
            },
            outputType,
            isPNG ? undefined : quality  // PNG doesn't support quality parameter
          );
        };

        // Start with quality 0.9
        tryCompress(0.9);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  };

  // Send small image directly (legacy type: 'image')
  const sendSmallImage = async (file: File, compressedBlob: Blob): Promise<boolean> => {
    const { ws: socket, cryptoKey } = useClipStore.getState();
    if (!socket || !cryptoKey) return false;

    const isPNG = file.type === 'image/png';

    // Read file as ArrayBuffer
    const arrayBuffer = await compressedBlob.arrayBuffer();

    // Convert to base64
    const base64Data = arrayBufferToBase64(arrayBuffer);

    // Encrypt the data
    const encryptedData = await encrypt(cryptoKey, base64Data);

    // Encrypt the filename
    const encryptedFilename = await encrypt(cryptoKey, file.name);

    // Send via WebSocket
    const message = {
      type: 'image',
      data: encryptedData,
      mimeType: isPNG ? 'image/png' : 'image/jpeg',
      filename: encryptedFilename,
      size: compressedBlob.size,
    };

    socket.send(JSON.stringify(message));
    return true;
  };

  // Send large image using new 3-step chunked protocol
  const sendLargeImage = async (file: File, compressedBlob: Blob, onProgress?: (progress: number) => void): Promise<boolean> => {
    const { ws: socket, cryptoKey, mySenderId } = useClipStore.getState();
    if (!socket || !cryptoKey) return false;

    const CHUNK_SIZE = 256 * 1024; // 256KB - smaller to avoid WebSocket limits
    const fileId = generateFileId();
    const totalChunks = Math.ceil(compressedBlob.size / CHUNK_SIZE);

    console.log('[sendLargeImage] Starting chunked transfer:', {
      fileId,
      totalChunks,
      totalSize: compressedBlob.size
    });

    // 1. Send image_start
    const encryptedFilename = await encrypt(cryptoKey, file.name);

    socket.send(JSON.stringify({
      type: 'image_start',
      message: {
        id: crypto.randomUUID(),
        type: 'image_start',
        fileId,
        totalSize: compressedBlob.size,
        totalChunks,
        mimeType: file.type,
        filename: encryptedFilename,
        sender: mySenderId || 'unknown',
        timestamp: Date.now(),
      },
    }));

    // 2. Send image_chunk messages
    for (let index = 0; index < totalChunks; index++) {
      const start = index * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, compressedBlob.size);
      const chunk = compressedBlob.slice(start, end);
      const arrayBuffer = await chunk.arrayBuffer();

      // Encrypt the chunk (already returns base64)
      const encryptedBase64 = await encrypt(cryptoKey, arrayBufferToBase64(arrayBuffer));

      socket.send(JSON.stringify({
        type: 'image_chunk',
        message: {
          id: crypto.randomUUID(),
          type: 'image_chunk',
          fileId,
          index,
          data: encryptedBase64,
          sender: mySenderId || 'unknown',
          timestamp: Date.now(),
        },
      }));

      // Report progress
      if (onProgress) {
        onProgress(Math.round(((index + 1) / totalChunks) * 100));
      }

      console.log(`[sendLargeImage] Sent chunk ${index + 1}/${totalChunks}`);
    }

    // 3. Send image_complete
    socket.send(JSON.stringify({
      type: 'image_complete',
      message: {
        id: crypto.randomUUID(),
        type: 'image_complete',
        fileId,
        sender: mySenderId || 'unknown',
        timestamp: Date.now(),
      },
    }));

    console.log('[sendLargeImage] Chunked transfer complete');
    return true;
  };

  const sendImage = async (file: File, onProgress?: (progress: number) => void) => {
    console.log('[sendImage] Starting, file:', file.name, 'type:', file.type, 'size:', file.size);
    const { ws: socket, cryptoKey, connected } = useClipStore.getState();

    console.log('[sendImage] State - socket:', !!socket, 'cryptoKey:', !!cryptoKey, 'connected:', connected);

    if (!socket || !cryptoKey || !connected) {
      console.error('[sendImage] Missing required state');
      return false;
    }

    try {
      // Compress image if needed
      console.log('[sendImage] Compressing image...');
      const compressedBlob = await compressImage(file, 900);
      console.log('[sendImage] Compressed size:', compressedBlob.size);

      // Check size: if <= 1MB, send directly; otherwise use chunked protocol
      const ONE_MB = 1 * 1024 * 1024;
      if (compressedBlob.size <= ONE_MB) {
        console.log('[sendImage] Using direct transfer (small image)');
        return sendSmallImage(file, compressedBlob);
      } else {
        console.log('[sendImage] Using chunked transfer (large image)');
        return sendLargeImage(file, compressedBlob, onProgress);
      }
    } catch (e) {
      console.error('[sendImage] Failed to send image:', e);
      throw e;
    }
  };

  /**
   * Send a file using chunked encryption transfer
   * Supports large images and videos
   */
  const sendFile = async (
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<boolean> => {
    console.log('[sendFile] Starting, file:', file.name, 'type:', file.type, 'size:', file.size);
    const { ws: socket, cryptoKey, connected } = useClipStore.getState();

    if (!socket || !cryptoKey || !connected) {
      console.error('[sendFile] Missing required state');
      return false;
    }

    // Validate file size (100MB max)
    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      console.error('[sendFile] File too large (max 100MB)');
      throw new Error('File too large (max 100MB)');
    }

    const fileId = generateFileId();
    const isVideo = file.type.startsWith('video/');

    try {
      // Encrypt filename
      const encryptedFilename = await encrypt(cryptoKey, file.name);

      // Generate thumbnail for videos
      let encryptedThumbnail: string | undefined;
      if (isVideo) {
        console.log('[sendFile] Generating video thumbnail...');
        const thumbnail = await generateVideoThumbnail(file);
        if (thumbnail) {
          encryptedThumbnail = await encrypt(cryptoKey, thumbnail);
        }
      }

      // Calculate total chunks
      const CHUNK_SIZE = 256 * 1024; // 256KB - smaller to avoid WebSocket limits
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      // Create manifest
      const manifest: FileManifest = {
        id: fileId,
        filename: encryptedFilename,
        mimeType: file.type,
        totalChunks,
        totalSize: file.size,
        thumbnail: encryptedThumbnail,
      };

      // Send manifest message
      console.log('[sendFile] Sending file manifest...');
      socket.send(
        JSON.stringify({
          type: 'file_manifest',
          message: {
            id: crypto.randomUUID(),
            type: 'file_manifest',
            manifest,
            sender: store.mySenderId || 'unknown',
            timestamp: Date.now(),
          },
        })
      );

      // Send chunks
      let sentChunks = 0;
      for await (const encryptedChunk of encryptFileInChunks(cryptoKey, file, CHUNK_SIZE)) {
        socket.send(
          JSON.stringify({
            type: 'file_chunk',
            message: {
              id: crypto.randomUUID(),
              type: 'file_chunk',
              data: encryptedChunk.data,
              chunkInfo: {
                index: encryptedChunk.index,
                total: encryptedChunk.total,
                fileId,
              },
              sender: store.mySenderId || 'unknown',
              timestamp: Date.now(),
            },
          })
        );

        sentChunks++;
        if (onProgress) {
          onProgress(Math.round((sentChunks / totalChunks) * 100));
        }
      }

      console.log('[sendFile] File sent successfully');
      return true;
    } catch (e) {
      console.error('[sendFile] Failed to send file:', e);
      throw e;
    }
  };

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    sendMessage,
    sendImage,
    sendFile,
    isConnected: store.connected,
    isConnecting: store.connecting,
  };
}
